import {
  PROPOSAL_STATUS_LABELS,
  type BeneficiarySituation,
  type CommissionResult,
  type ExtractedProposal,
  type ProposalStatus,
  type ProposalType,
  type SalesKpis,
  type SalesSettingsValues,
} from '@sales-rules';
import { api } from './api';

/**
 * Módulo Vendas no front: tipos, chamadas e formatações. As regras (comissão,
 * paradas, validação…) vêm de `@sales-rules`, o mesmo arquivo da API.
 */

export interface SellerRef {
  membershipId: string;
  name: string;
}

export interface SalesBeneficiary {
  name: string;
  cpf: string | null;
  monthlyCents: number;
  situation: BeneficiarySituation | null;
  relationship: string | null;
  notes: string | null;
}

/** Entrevista médica: horários `AAAA-MM-DDTHH:MM` de Fortaleza. */
export interface SalesInterview {
  id: string;
  who: string;
  at: string;
  notifyAt: string;
  done: boolean;
  remindedAt: string | null;
  notifiedAt: string | null;
}

export interface SalesProposal {
  id: string;
  requestNumber: string | null;
  company: string;
  cnpj: string | null;
  effectiveDate: string;
  type: ProposalType;
  plan: string | null;
  operator: string;
  seller: SellerRef | null;
  sellerName: string | null;
  lives: number;
  livesManual: boolean;
  status: ProposalStatus;
  pendingText: string | null;
  statusSince: string;
  notes: string | null;
  economyEnergyAt: string | null;
  version: number;
  leadId: string | null;
  businessId: string | null;
  createdAt: string;
  updatedAt: string;
  pdf: { fileName: string; size: number | null; uploadedAt: string | null } | null;
  interviews: SalesInterview[];
  /** Quem negociou (o lead do CRM), que não precisa ser beneficiário. */
  lead: { id: string; name: string } | null;
  beneficiaries: SalesBeneficiary[];
  history?: Array<{
    fromStatus: ProposalStatus | null;
    toStatus: ProposalStatus;
    changedAt: string;
    changedBy: { membershipId: string | null; name: string | null };
  }>;
}

export interface ProposalPayload {
  requestNumber?: string | null;
  company: string;
  cnpj?: string | null;
  effectiveDate: string;
  type: ProposalType;
  plan?: string | null;
  operator: string;
  sellerMembershipId?: string;
  lives?: number | null;
  status: ProposalStatus;
  pendingText?: string | null;
  notes?: string | null;
  leadId?: string | null;
  businessId?: string | null;
  beneficiaries: Array<{
    name: string;
    cpf?: string | null;
    monthlyCents: number;
    situation: BeneficiarySituation;
    relationship?: string | null;
    notes?: string | null;
  }>;
}

export interface SalesSummary {
  month: string;
  seller?: SellerRef | null;
  commission?: CommissionResult;
  bySeller?: Array<{
    seller: SellerRef | null;
    sellerName: string | null;
    commission: CommissionResult;
    open: { proposals: number; lives: number };
  }>;
  team?: { cleanLives: number; totalCents: number; projectedCents: number };
  kpis: SalesKpis;
}

export interface SalesTrashItem {
  id: string;
  requestNumber: string | null;
  company: string;
  effectiveDate: string;
  status: ProposalStatus;
  seller: SellerRef | null;
  deletedAt: string;
  purgeAfter: string;
}

export interface ImportPreview {
  sheet?: string;
  proposals: number;
  beneficiaries: number;
  skipped: Array<{ row: number; reason: string }>;
  sellers?: Array<{ name: string; proposals: number; suggestion: string | null }>;
}

export interface ImportResult {
  created: number;
  updated: number;
  skipped: Array<{ row: number; reason: string }>;
}

type Page<T> = { count: number; data: T[] };

export interface SalesPrefill {
  businessId: string;
  leadId: string;
  leadName: string;
  company: string;
}

export const salesApi = {
  list: async () => (await api.get<Page<SalesProposal>>('/sales/proposals')).data.data,
  get: async (id: string) => (await api.get<SalesProposal>(`/sales/proposals/${id}`)).data,
  create: async (payload: ProposalPayload) =>
    (await api.post<SalesProposal>('/sales/proposals', payload)).data,
  update: async (id: string, payload: ProposalPayload & { version: number }) =>
    (await api.put<SalesProposal>(`/sales/proposals/${id}`, payload)).data,
  status: async (id: string, status: ProposalStatus, pendingText?: string) =>
    (await api.patch<SalesProposal>(`/sales/proposals/${id}/status`, { status, pendingText }))
      .data,
  economyEnergy: async (id: string, indicated: boolean) =>
    (await api.patch<SalesProposal>(`/sales/proposals/${id}/economy-energy`, { indicated }))
      .data,
  remove: async (id: string) => {
    await api.delete(`/sales/proposals/${id}`);
  },
  duplicates: async (number: string) =>
    (
      await api.get<Page<Pick<SalesProposal, 'id' | 'company' | 'effectiveDate' | 'status'>>>(
        '/sales/proposals/duplicates',
        { params: { number } },
      )
    ).data.data,
  summary: async (month: string, seller?: string) =>
    (await api.get<SalesSummary>('/sales/summary', { params: { month, seller } })).data,
  sellers: async () =>
    (await api.get<Array<SellerRef & { isManager: boolean }>>('/sales/sellers')).data,
  settings: async () =>
    (await api.get<SalesSettingsValues & { updatedAt: string | null }>('/sales/settings')).data,
  trash: async () => (await api.get<Page<SalesTrashItem>>('/sales/trash')).data.data,
  restore: async (id: string) =>
    (await api.post<SalesProposal>(`/sales/trash/${id}/restore`)).data,
  purge: async (id: string) => {
    await api.delete(`/sales/trash/${id}`);
  },
  previewSheet: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post<ImportPreview>('/sales/import/xlsx/preview', form)).data;
  },
  importSheet: async (file: File, sellerMap?: Record<string, string | null>) => {
    const form = new FormData();
    form.append('file', file);
    if (sellerMap) form.append('sellerMap', JSON.stringify(sellerMap));
    return (await api.post<ImportResult>('/sales/import/xlsx', form)).data;
  },
  previewArtifact: async (proposals: unknown[]) =>
    (await api.post<ImportPreview>('/sales/import/artifact/preview', { proposals })).data,
  importArtifact: async (proposals: unknown[], sellerMap?: Record<string, string | null>) =>
    (await api.post<ImportResult>('/sales/import/artifact', { proposals, sellerMap })).data,
  /** Leitura do PDF pela IA no servidor (só quando o leitor da Unimed não fecha). */
  extractPdf: async (file: File, signal: AbortSignal) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post<ExtractedProposal>('/sales/pdf/extract', form, { signal })).data;
  },
  attachPdf: async (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post<SalesProposal>(`/sales/proposals/${id}/pdf`, form)).data;
  },
  pdfBlob: async (id: string) =>
    (await api.get<Blob>(`/sales/proposals/${id}/pdf`, { responseType: 'blob' })).data,
  /** Para começar a proposta a partir de um negócio do CRM. */
  prefillFromBusiness: async (businessId: string) =>
    (await api.get<SalesPrefill>(`/sales/prefill/business/${businessId}`)).data,
  /** Propostas de um lead (ficha do lead), no escopo de quem vê. */
  listByLead: async (leadId: string) =>
    (await api.get<Page<SalesProposal>>('/sales/proposals', { params: { leadId } })).data.data,
  createInterview: async (
    proposalId: string,
    body: { who: string; at: string; notifyAt: string },
  ) => (await api.post<SalesProposal>(`/sales/proposals/${proposalId}/interviews`, body)).data,
  updateInterview: async (
    id: string,
    body: { done?: boolean; reminded?: boolean; snoozeMinutes?: number },
  ) => (await api.patch<SalesProposal>(`/sales/interviews/${id}`, body)).data,
  deleteInterview: async (id: string) =>
    (await api.delete<SalesProposal>(`/sales/interviews/${id}`)).data,
  exportSheet: async (seller?: string) =>
    (await api.get<Blob>('/sales/export/xlsx', { params: { seller }, responseType: 'blob' }))
      .data,
};

// ------------------------------------------------------------ formatação

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
export const brl = (cents: number): string => BRL.format(cents / 100);

/** "AAAA-MM-DD" → "dd/mm/aaaa". */
export const dmy = (iso: string | null | undefined): string =>
  iso && /^\d{4}-\d{2}-\d{2}/.test(iso)
    ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`
    : '—';

/** "dd/mm/aaaa" (ou "dd/mm", ano atual) → "AAAA-MM-DD"; null se a data não existe. */
export function parseDmy(input: string, today: string): string | null {
  const value = input.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const match = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(value);
  if (!match) return null;
  let year = match[3] ? Number(match[3]) : Number(today.slice(0, 4));
  if (year < 100) year += 2000;
  const month = Number(match[2]);
  const day = Number(match[1]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Máscara de digitação dd/mm/aaaa. */
export const maskDate = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  let out = digits.slice(0, 2);
  if (digits.length > 2) out += `/${digits.slice(2, 4)}`;
  if (digits.length > 4) out += `/${digits.slice(4)}`;
  return out;
};

export const formatCpf = (digits: string | null | undefined): string => {
  const value = (digits ?? '').replace(/\D/g, '');
  if (value.length !== 11) return digits ?? '';
  return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(6, 9)}-${value.slice(9)}`;
};

export const maskCpf = (input: string): string => {
  const digits = input.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

export const formatCnpj = (digits: string | null | undefined): string => {
  const value = (digits ?? '').replace(/\D/g, '');
  if (value.length !== 14) return digits ?? '';
  return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8, 12)}-${value.slice(12)}`;
};

export const maskCnpj = (input: string): string => {
  const digits = input.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

/** Reais digitados ("351,33") → centavos. */
export const toCents = (input: string): number => {
  let cleaned = input.replace(/[^\d,.]/g, '');
  if (cleaned.includes(',')) cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? Math.round(value * 100) : 0;
};

export const centsToInput = (cents: number): string =>
  cents ? (cents / 100).toFixed(2).replace('.', ',') : '';

export const typeLabel = (type: ProposalType): string => (type === 'ADESAO' ? 'ADESÃO' : type);
export const statusLabel = (status: ProposalStatus): string => PROPOSAL_STATUS_LABELS[status];

const MONTHS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];
export const monthName = (month: string): string =>
  `${MONTHS[Number(month.slice(5, 7)) - 1]}/${month.slice(0, 4)}`;

/** "(Titular)" ou "(Dependente - Filho)". */
export function relationshipTag(relationship: string | null): string {
  const value = (relationship ?? '').trim().toUpperCase();
  if (!value) return '';
  if (value === 'TITULAR') return '(Titular)';
  const name = value.replace(/\(A\)/g, '').trim().toLowerCase();
  if (!name || name === 'dependente') return '(Dependente)';
  return `(Dependente - ${name.charAt(0).toUpperCase()}${name.slice(1)})`;
}

/** "vigência em 4 dias" / "vigência há 2 dias" / "vigência hoje" (como no artefato). */
export function relativeEffective(days: number | null): string {
  if (days === null) return 'sem vigência';
  if (days === 0) return 'vigência hoje';
  if (days === 1) return 'vigência amanhã';
  if (days > 0) return `vigência em ${days} dias`;
  if (days === -1) return 'vigência foi ontem';
  return `vigência há ${-days} dias`;
}

/** Classe da operadora no chip (cores do artefato). */
export const operatorClass = (operator: string): string =>
  `op-${operator
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

/** Copiar para a área de transferência, sem quebrar onde não há permissão. */
export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

/** Preferências deste aparelho (filtros, últimos valores do formulário). */
export const devicePrefs = {
  get(key: string): string {
    try {
      return localStorage.getItem(`vendas.${key}`) ?? '';
    } catch {
      return '';
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(`vendas.${key}`, value);
    } catch {
      // Navegador sem armazenamento: só não lembra.
    }
  },
};
